import { motion } from "framer-motion";
import {
  Briefcase, Building2, TrendingUp, User, Shield, Wallet,
} from "lucide-react";

const services = [
  {
    icon: Briefcase,
    title: "Working Capital",
    desc: "Flexible cash to cover payroll, inventory, and everyday operational needs.",
    range: "$5K – $500K",
    highlight: true,
  },
  {
    icon: Wallet,
    title: "Merchant Cash Advance",
    desc: "Get a lump sum today and repay from future card sales — no fixed schedule.",
    range: "$10K – $2M",
  },
  {
    icon: Building2,
    title: "SBA Loans",
    desc: "Government-backed long-term loans at low fixed rates for serious growth.",
    range: "Up to $5M",
  },
  {
    icon: TrendingUp,
    title: "Business Lines of Credit",
    desc: "Draw funds on-demand, only pay interest on what you use.",
    range: "$10K – $250K",
  },
  {
    icon: User,
    title: "Personal Loans",
    desc: "Fast personal funding for entrepreneurs, contractors, and individuals.",
    range: "$1K – $100K",
  },
  {
    icon: Shield,
    title: "Credit Repair",
    desc: "We work with the bureaus to challenge errors and rebuild your score.",
    range: "From $99/mo",
  },
];

export default function Services({ onApply }) {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-slate-50 relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-purple-600">
              What we fund
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black mt-3 tracking-tight text-slate-900 max-w-2xl">
              Capital solutions built around your business.
            </h2>
          </div>
          <p className="text-slate-500 max-w-md text-lg">
            Six funding products. One team. Match the right capital to the
            moment — from cash-flow bridges to seven-figure growth financing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, idx) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
              data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-").replace(/\//g, "")}`}
              className={`group relative rounded-3xl p-7 border transition-all hover:-translate-y-1 hover:shadow-xl cursor-pointer ${
                s.highlight
                  ? "bg-gradient-to-br from-[#0b1121] to-[#312e81] text-white border-transparent shadow-2xl shadow-purple-500/20 lg:col-span-2 lg:row-span-1"
                  : "bg-white text-slate-900 border-slate-100 hover:border-purple-200"
              }`}
              onClick={() => onApply?.({ funding_purpose: s.title })}
            >
              <div
                className={`w-12 h-12 rounded-2xl grid place-items-center mb-5 ${
                  s.highlight
                    ? "bg-white/10 text-purple-300"
                    : "bg-purple-100 text-purple-700 group-hover:bg-purple-600 group-hover:text-white transition-colors"
                }`}
              >
                <s.icon className="w-6 h-6" />
              </div>
              <h3
                className={`font-display text-2xl font-bold mb-2 ${
                  s.highlight ? "text-white" : "text-slate-900"
                }`}
              >
                {s.title}
              </h3>
              <p
                className={`text-sm leading-relaxed ${
                  s.highlight ? "text-white/70" : "text-slate-500"
                }`}
              >
                {s.desc}
              </p>
              <div
                className={`mt-6 pt-4 border-t flex items-center justify-between ${
                  s.highlight ? "border-white/10" : "border-slate-100"
                }`}
              >
                <span
                  className={`text-xs font-bold uppercase tracking-wider ${
                    s.highlight ? "text-purple-300" : "text-purple-600"
                  }`}
                >
                  {s.range}
                </span>
                <span
                  className={`text-xs font-semibold uppercase tracking-wider ${
                    s.highlight ? "text-white/60" : "text-slate-400"
                  } group-hover:text-purple-600 transition`}
                >
                  Apply →
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
